import React, { useEffect, useState, useMemo } from 'react';
import { useCachedBlobUrl } from '../../Services/useBlobDownloader';
import MessageArchiveDownload, { hasArchivableAttachments } from './MessageArchiveDownload';

export const FileRender = ({ message }) => {
  const { getCachedBlobUrl } = useCachedBlobUrl();
  const [hrefMap, setHrefMap] = useState({});

  const files = useMemo(
    () => (Array.isArray(message?.files) ? message.files : []),
    [message?.files],
  );

  const filesFingerprint = useMemo(() => JSON.stringify(files.map((f) => f?.key ?? '')), [files]);

  useEffect(() => {
    let alive = true;
    const ac = new AbortController();

    (async () => {
      if (!files.length || !getCachedBlobUrl) {
        if (alive) setHrefMap({});
        return;
      }

      try {
        const pairs = await Promise.all(
          files.map(async (file) => {
            const k = file?.key;
            if (!k) return [k, undefined];
            try {
              const blobUrl = await getCachedBlobUrl(k, 'file', {
                signal: ac.signal,
              });
              return [k, blobUrl || undefined];
            } catch {
              return [k, undefined];
            }
          }),
        );

        if (!alive) return;
        setHrefMap(Object.fromEntries(pairs.filter(([k]) => k)));
      } catch (e) {
        if (alive) {
          console.error('Failed to build hrefMap', e);
          setHrefMap({});
        }
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
  }, [files, filesFingerprint, getCachedBlobUrl]);

  const allReady =
    files.length > 0 &&
    files.every((f) => {
      if (!f.key) return true;
      return !!hrefMap[f.key];
    });

  const isPdf = (file) => (file?.extension || '').toLowerCase() === 'pdf';

  const openFile = (file) => {
    const href = file.key ? hrefMap[file.key] : file.base64 || file.url;
    if (!href) return;

    const a = document.createElement('a');
    a.href = href;
    if (isPdf(file)) {
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
    } else {
      a.download = file.fileName || 'file';
    }
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const openAll = () => {
    files.forEach((f) => openFile(f));
  };

  const useArchive = hasArchivableAttachments({ files });

  return (
    <>
      {useArchive && <MessageArchiveDownload files={files} />}

      {files.length > 0 && !useArchive && (
        <div
          className={`file-download-link ${!allReady ? 'is-loading' : ''}`}
          onClick={allReady ? openAll : undefined}
          aria-label={allReady ? 'Download file' : undefined}
          aria-busy={!allReady ? 'true' : undefined}
          aria-disabled={!allReady ? 'true' : undefined}
          title={!allReady ? 'Loading…' : 'Download file'}
        >
          {!allReady && (
            <svg width="20" height="20" viewBox="0 0 50 50">
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray="31.4 188.4"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 25 25"
                  to="360 25 25"
                  dur="0.9s"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
          )}
        </div>
      )}

      {files.map((f, idx) => {
        const fileName = f.fileName || 'unknown';
        const size = f.size || '';
        const extension = (f.extension || '').toLowerCase();

        return (
          <div
            key={idx}
            className={`file-wrapper file-${extension} ${allReady ? 'clickable' : ''}`}
            onClick={() => allReady && openFile(f)}
          >
            <div className="file-info">
              <div className="file-name">{fileName}</div>

              <div className="file-creds">
                <span className="file-ext">{extension.toUpperCase()}</span>
                <span> Document</span>
                <span className="file-separator" />

                {size && (
                  <>
                    <span className="file-size">{size}</span>
                    <span> MB</span>
                  </>
                )}
              </div>
            </div>

            <div className="file-icon-wrapper">
              <div className="file-icon" />
            </div>
          </div>
        );
      })}
    </>
  );
};
